import { useEffect, useRef, type CSSProperties } from "react";
import { prefersReducedMotion } from "@/utils/motion";
import "./BuildSequence.css";

const checks = [
  "Component A: Qualified",
  "Component B: Qualified",
  "Connection: Valid",
  "Dependencies: Resolved",
];

/**
 * "See Devory Build" — a simulated qualification run, not a marketing
 * animation. It shows the same thing the Qualification page describes in
 * prose: components checked, a connection validated, a graph assembled, a
 * package produced. Plays once on scroll-into-view; reduced motion shows the
 * finished run immediately.
 */
export function BuildSequence() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion() || typeof IntersectionObserver === "undefined") return;

    el.classList.add("build-sequence-pending");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          el.classList.add("build-sequence-playing");
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="build-sequence"
      role="img"
      aria-label="A simulated qualification run: two components are qualified, a connection is validated, dependencies are resolved, the graph is assembled, and an executable package is created."
    >
      <ul className="build-sequence-lines">
        {checks.map((line, index) => (
          <li key={line} className="build-sequence-line" style={{ "--i": index } as CSSProperties}>
            <svg
              className="build-sequence-check"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M8 12.5l2.75 2.75L16 9.75" />
            </svg>
            <span>{line}</span>
          </li>
        ))}
      </ul>
      <div className="build-sequence-graph" style={{ "--i": checks.length } as CSSProperties}>
        <span className="build-sequence-graph-label">Graph</span>
        <div className="build-sequence-bar">
          <div className="build-sequence-bar-fill" />
        </div>
      </div>
      <p className="build-sequence-result" style={{ "--i": checks.length + 1 } as CSSProperties}>
        Executable package created
      </p>
    </div>
  );
}
